import { ThunkDispatch } from 'redux-thunk';
import { IMessageEvent, w3cwebsocket as W3CWebSocket } from 'websocket';
import 'eventsource/example/eventsource-polyfill';

import {
    isDefined,
    isAdminPortal,
    getValueFromKey,
} from '@eon-home/react-library';

import {
    GridXHeatPumpDeviceModel,
    HeatPumpApi,
    ResponseError,
    SmartHomeDeviceModelTypeEnum,
} from '@swagger-http';
import { EventsResponseModel, SmartHomeResponseModel } from '@swagger-sse/api';

import { EventsFactory } from '@store/actions';
import {
    BatteryStatus,
    LiveDataEventTypes,
    LiveDataActionTypes,
    MessageTypeEnumExtended,
    NotificationsActionTypes,
    WebsocketMessageSubTypeEnum,
} from '@store/enums';
import {
    WebSocketData,
    LiveDataEvent,
    LiveDataAction,
    NotificationsResponseModel,
    RootState,
    HeatPumpTelemetry,
} from '@store/types';
import {
    debug,
    toBase64,
    handleError,
    isSwedishUser,
    getAccessToken,
    checkForScopes,
    createRequestConfiguration,
} from '@tools/utils';
import moment from 'moment';

import MessageTypeEnum = EventsResponseModel.MessageTypeEnum;
import { Scope } from '@tools/enums';
import { store } from '@src/index';

type AppDispatch = ThunkDispatch<RootState, unknown, LiveDataAction>;

// eslint-disable-next-line no-var
export var webSocket: W3CWebSocket | undefined;

export function getBatteryState(batteryPower: number): BatteryStatus {
    if (batteryPower === 0) {
        return BatteryStatus.Idle;
    } else if (batteryPower > 0) {
        return BatteryStatus.Charging;
    } else {
        return BatteryStatus.Discharging;
    }
}

export function getHeatPumpConnectionStatus(
    configId: string,
): (dispatch: AppDispatch) => Promise<LiveDataAction | void> {
    if (!checkForScopes([Scope.ENERGYDEVICES_HEAT_PUMP_READ])) {
        return () => Promise.resolve();
    }

    return (dispatch: AppDispatch) =>
        new HeatPumpApi(createRequestConfiguration())
            .heatPumpGetGridXHeatPump({ configId })
            .then((data: GridXHeatPumpDeviceModel) => {
                const lastUpdated =
                    store.getState().liveData.telemetries.heatpump.lastUpdated;
                const contactedAt = data.connectionStatus?.contactedAt;
                let payload: Partial<HeatPumpTelemetry> = {};

                if (
                    !lastUpdated ||
                    (lastUpdated &&
                        contactedAt &&
                        new Date(lastUpdated) < new Date(contactedAt))
                ) {
                    payload = {
                        error: data.connectionStatus?.status !== 'AVAILABLE',
                        lastUpdated: contactedAt,
                    };
                }

                return dispatch({
                    type: LiveDataActionTypes.HEAT_PUMP_TELEMETRIES,
                    payload,
                });
            })
            .catch(async (e: ResponseError) => {
                await handleError(e, 'Error when getting the heat pump data:');
            });
}

export function processSolarPanelLiveData<T>(
    data: T,
    dispatch: AppDispatch,
): void {
    const batteryPower = getValueFromKey(
        data,
        'data.battery.power.total',
    ) as number;

    dispatch({
        type: LiveDataActionTypes.PVBATTERY_TELEMETRIES,
        payload: {
            // PV
            pv2home: getValueFromKey(data, 'data.PV.energy.PV2home.total'),
            pvGeneration: getValueFromKey(data, 'data.PV.power.total'),

            // Battery
            battery2home: getValueFromKey(
                data,
                'data.battery.energy.battery2home.total',
            ),
            home2battery: getValueFromKey(
                data,
                'data.battery.energy.home2battery.total',
            ),
            batteryCharge: getValueFromKey(
                data,
                'data.battery.energy.stateOfCharge',
            ),
            batteryPower,
            batteryStatus: getBatteryState(batteryPower),
            batteryCurrent: getValueFromKey(data, 'data.battery.current.total'),

            // Inverter
            inverterPower: getValueFromKey(data, 'data.inverter.power.total'),
            inverterEnergyIn: getValueFromKey(data, 'data.inverter.energy.in'),
            inverterEnergyOut: getValueFromKey(
                data,
                'data.inverter.energy.out',
            ),

            // Meta
            timestamp: getValueFromKey(data, 'creationTime'),
            error: false,
            loading: false,

            // Hide the empty Energy Flow state
            hasLiveData: true,
        },
    });
}

export function processMeterLiveData<T>(
    data: T,
    dispatch: AppDispatch,
): LiveDataAction {
    return dispatch({
        type: LiveDataActionTypes.GCP_TELEMETRIES,
        payload: {
            // Current
            currentL1: getValueFromKey(data, 'data.current.L1'),
            currentL2: getValueFromKey(data, 'data.current.L2'),
            currentL3: getValueFromKey(data, 'data.current.L3'),

            // Energy
            grid2home: getValueFromKey(data, 'data.energy.grid2home.total'),
            home2grid: getValueFromKey(data, 'data.energy.home2grid.total'),
            energyConsumption: getValueFromKey(
                data,
                'data.energy.consumption.total',
            ),
            energySelfConsumption: getValueFromKey(
                data,
                'data.energy.selfConsumption.total',
            ),

            // Solar Cloud
            solarCloudBalance: getValueFromKey(
                data,
                'data.energy.solarCloud.balance',
            ),
            solarCloudBalanceTimestamp: getValueFromKey(
                data,
                'data.energy.solarCloud.readingTimestamp',
            ),

            // Power
            power: getValueFromKey(data, 'data.power.total'),
            powerConsumption: getValueFromKey(
                data,
                'data.power.consumption.total',
            ),
            powerSelfConsumption: getValueFromKey(
                data,
                'data.power.selfConsumption.total',
            ),
            deviceId: getValueFromKey(data, 'deviceId'),

            // Meta
            error: false,
            loading: false,
            timestamp: getValueFromKey(data, 'creationTime'),
            hasLiveData: true,
        },
    });
}

export function processSmartHomeLiveData<T extends SmartHomeResponseModel>(
    data: T | void,
    dispatch: AppDispatch,
): LiveDataAction {
    if (isSwedishUser()) {
        return dispatch({
            type: LiveDataActionTypes.SMART_PLUGS_TELEMETRIES,
            payload: {},
        });
    }

    if (
        data &&
        data.deviceType ===
            (SmartHomeDeviceModelTypeEnum.Socket as unknown as SmartHomeResponseModel.DeviceTypeEnum)
    ) {
        const power = getValueFromKey(data, 'data.power.total');
        const energy = getValueFromKey(data, 'data.energy.total');
        const deviceId = getValueFromKey(data, 'deviceId');
        const timestamp = getValueFromKey(data, 'creationTime');

        if (power) {
            return dispatch({
                type: LiveDataActionTypes.SMART_PLUGS_TELEMETRIES,
                payload: {
                    power,
                    deviceId,
                    timestamp,
                },
            });
        }

        if (energy) {
            return dispatch({
                type: LiveDataActionTypes.SMART_PLUGS_TELEMETRIES,
                payload: {
                    energy,
                    deviceId,
                    timestamp,
                },
            });
        }
    }

    return dispatch({
        type: LiveDataActionTypes.SMART_PLUGS_TELEMETRIES,
        payload: {},
    });
}

export function processHeatingCoolingLiveData<T>(
    data: T,
    dispatch: AppDispatch,
): LiveDataAction {
    const temperature: Record<string, number> = {};

    if (isDefined(data, 'data.temperature.celsius')) {
        temperature.celsius = getValueFromKey(
            data,
            'data.temperature.celsius',
        ) as number;
    } else if (isDefined(data, 'data.temperature.inside')) {
        temperature.celsius = getValueFromKey(
            data,
            'data.temperature.inside',
        ) as number;
    }

    if (isDefined(data, 'data.temperature.fahrenheit')) {
        temperature.fahrenheit = getValueFromKey(
            data,
            'data.temperature.fahrenheit',
        ) as number;
    }

    return dispatch({
        type: LiveDataActionTypes.HEATING_COOLING_TELEMETRIES,
        payload: {
            active: true,
            deviceId: (getValueFromKey(data, 'deviceId') as string).split(
                ':',
            )[0],
            timestamp: getValueFromKey(data, 'creationTime'),
            temperature,
        },
    });
}

export function processWallboxLiveData<T>(
    data: T,
    dispatch: AppDispatch,
): LiveDataAction {
    return dispatch({
        type: LiveDataActionTypes.WALLBOX_TELEMETRIES,
        payload: {
            power: getValueFromKey(data, 'data.power.home2EV.session'),
            timestamp: getValueFromKey(data, 'creationTime'),
            consumption: getValueFromKey(data, 'data.energy.home2EV.session'),
            telemetrySessionId: getValueFromKey(data, 'data.chargingSessionID'),
            telemetryTimestamp: getValueFromKey(data, 'creationTime'),
        },
    });
}

export function processElectricCarLiveData<T>(
    data: T,
    dispatch: AppDispatch,
): LiveDataAction {
    return dispatch({
        type: LiveDataActionTypes.ELECTRIC_CAR_TELEMETRIES,
        payload: {
            isElectricCarOnline: true, // TODO as soon as BE is ready: getValueFromKey(data, 'data.isOnline'),
            isElectricCarHome: getValueFromKey(data, 'data.isHome'),
            evStateLoading: false,
            isElectricCarPluggedIn: getValueFromKey(data, 'data.isPluggedIn'),
            isElectricCarCharging: getValueFromKey(data, 'data.isCharging'),
            electricCarRange: getValueFromKey(data, 'data.range'),
            electricCarBatteryLevel: getValueFromKey(data, 'data.batteryLevel'),
            timestamp: getValueFromKey(data, 'creationTime'),
            telemetryTimestamp: getValueFromKey(data, 'creationTime'),
        },
    });
}

export function processAlarmsLiveData<T extends NotificationsResponseModel>(
    data: T | void,
    dispatch: AppDispatch,
): LiveDataAction {
    if (!data) {
        return dispatch({
            type: LiveDataActionTypes.EVENT_NOTIFICATION,
            payload: {},
        });
    }

    if (data.messageType === MessageTypeEnum.Alert) {
        const liveDataEvent: LiveDataEvent = new EventsFactory().getEvent(data);
        const action = liveDataEvent.getAction();

        if (action.payload) {
            return dispatch(action);
        }
    }

    if (data.messageType === MessageTypeEnum.Notification) {
        return dispatch({
            type: LiveDataActionTypes.EVENT_NOTIFICATION,
            payload: {
                id: getValueFromKey(data, 'id'),
                code: getValueFromKey(data, 'messageSubType'),
                date: getValueFromKey(data, 'creationTime'),
                read: getValueFromKey(data, 'ack'),
                type: getValueFromKey(data, 'messageType'),
                variables: getValueFromKey(data, 'data.variables'),
            },
        });
    }

    if (data.messageType === MessageTypeEnumExtended.EVSE_FIRMWARE) {
        return dispatch({
            type: NotificationsActionTypes.SET_EMOB_FIRMWARE_NOTIFICATION,
            payload: getValueFromKey(data, 'data'),
        });
    }

    if (
        data.messageType === MessageTypeEnumExtended.EVSE_SECURITY_NOTIFICATION
    ) {
        return dispatch({
            type: NotificationsActionTypes.UPDATE_TAMPER_NOTIFICATION,
            payload: getValueFromKey(data, 'data'),
        });
    }

    if (
        data.messageType === MessageTypeEnumExtended.ELECTRIC_CAR_EVENT &&
        data.messageSubType === WebsocketMessageSubTypeEnum.ENODE_ACTION &&
        data.creationTime &&
        data.creationTime >= moment().unix() - 30 * 60 // events that are older than 30 min should not be processed
    ) {
        return dispatch({
            type: LiveDataActionTypes.UPDATE_ELECTRIC_CAR_TELEMETRIES,
            payload: data.data,
        });
    }

    return dispatch({
        type: LiveDataActionTypes.EVENT_NOTIFICATION,
        payload: {},
    });
}

export function processGatewayLiveData<T>(
    payload: T,
    dispatch: AppDispatch,
): LiveDataAction {
    return dispatch({
        type: LiveDataActionTypes.GATEWAY_STATUSES,
        payload,
    });
}

export function processHeatPumpLiveData<T>(
    data: T,
    dispatch: AppDispatch,
): LiveDataAction {
    return dispatch({
        type: LiveDataActionTypes.HEAT_PUMP_TELEMETRIES,
        payload: {
            powerConsumption: getValueFromKey(data, 'data.heatPump'),
            timestamp: getValueFromKey(data, 'creationTime'),
            telemetryTimestamp: getValueFromKey(data, 'creationTime'),
        },
    });
}

export function handleWebSocketData(
    data: WebSocketData[],
    dispatch: AppDispatch,
) {
    debug.log('📡📡📡 WebSocket message received:', data);

    // The expected data in the websocket message
    // is an the form of an Array.
    // Anything other than an Array is just discarded.
    if (!Array.isArray(data) || !data.length) {
        return;
    }

    let result;

    for (const item of data) {
        const topic: LiveDataEventTypes | undefined = item.topic;
        switch (topic) {
            case LiveDataEventTypes.PVBATTERY_TELEMETRIES:
                result = processSolarPanelLiveData(item, dispatch);
                break;
            case LiveDataEventTypes.GCP_TELEMETRIES:
                result = processMeterLiveData(item, dispatch);
                break;
            case LiveDataEventTypes.SMARTHOME_TELEMETRIES:
                result = processSmartHomeLiveData(
                    item as unknown as SmartHomeResponseModel,
                    dispatch,
                );
                break;
            case LiveDataEventTypes.HEATING_COOLING_TELEMETRIES:
                result = processHeatingCoolingLiveData(item, dispatch);
                break;
            case LiveDataEventTypes.WALLBOX_TELEMETRIES:
                result = processWallboxLiveData(item, dispatch);
                break;
            case LiveDataEventTypes.ELECTRIC_CAR_TELEMETRIES:
                result = processElectricCarLiveData(item, dispatch);
                break;
            case LiveDataEventTypes.EVENTS:
                result = processAlarmsLiveData(
                    item as NotificationsResponseModel,
                    dispatch,
                );
                break;
            case LiveDataEventTypes.GATEWAY_STATUSES:
                result = processGatewayLiveData(item, dispatch);
                break;
            case LiveDataEventTypes.HEAT_PUMP_TELEMETRIES:
                result = processHeatPumpLiveData(item, dispatch);
                break;
            default:
                result = undefined;
                break;
        }
    }

    return result;
}

export function connectWebSocket(
    token: string,
    dispatch: AppDispatch,
): W3CWebSocket | void {
    try {
        if (!webSocket) {
            webSocket = new W3CWebSocket(process.env.WEBSOCKET_URL_DEFAULT, [
                'x-api-key',
                toBase64(token).replace(isAdminPortal() ? '==' : '', ''),
            ]);
        }
    } catch (e) {
        handleError(e, 'Failed connecting to WebSocket:');
    }

    if (!webSocket) {
        return webSocket;
    }

    webSocket.onopen = () => debug.log('🎉🎉🎉 Connected to WebSocket! 🎉🎉🎉');

    webSocket.onclose = () => {
        debug.log('💀💀💀 Disconnected from WebSocket! 💀💀💀');

        !!webSocket && connectWebSocket(token, dispatch);
    };

    webSocket.onmessage = (message: IMessageEvent) => {
        if (typeof message.data !== 'string') {
            return;
        }

        const data: WebSocketData[] = JSON.parse(message.data);

        return handleWebSocketData(data, dispatch);
    };

    return webSocket;
}

export function disconnectWebSocket(): void {
    if (!webSocket) {
        return;
    }

    webSocket.close();
    webSocket = undefined;
}

export function connectLiveData() {
    return function (
        dispatch: AppDispatch,
    ): Array<EventSource | W3CWebSocket | void> {
        const accessToken: string = getAccessToken();

        return [webSocket || connectWebSocket(accessToken, dispatch)];
    };
}

export function disconnectLiveData(): void {
    disconnectWebSocket();
}

export function reconnectLiveData(dispatch: AppDispatch): void {
    disconnectLiveData();
    dispatch(connectLiveData());
}
